<template>
  <div class="page-wrapper">
    <div class="page-content--bge5">
      <div class="container">
        <div class="login-wrap">
          <div class="login-content" v-if="!registered">
            <div class="login-logo">
              <img alt="NP-CDN" src="/images/logo.png" class="sidebar-logo">
            </div>
            <div class="login-form">
              <div class="text-center" style="color: red;" v-if="error">{{ text(error) }}</div>
              <form @submit.prevent="onSubmit">
                <div class="form-group">
                  <label for="email">{{ text('email') }}*</label>
                  <input @change="$v.form.email.$touch()"
                         autocomplete="username email"
                         class="au-input au-input--full"
                         id="email"
                         :placeholder="text('enter_email')"
                         type="email"
                         v-model.trim="form.email">
                  <small class="form-text text-danger" v-if="$v.form.email.$dirty">
                    <span v-if="!$v.form.email.required">
                      {{ text('non_empty_err') }}
                    </span>
                    <span v-else-if="!$v.form.email.email">
                      {{ text('valid_email_err') }}
                    </span>
                    <span v-else-if="!$v.form.email.isUnique && !isEmailUnique">
                      {{ text('unique_email_err') }}
                    </span>
                  </small>
                </div>
                <div class="row form-group">
                  <div class="col-6">
                    <label for="name">
                      {{ text('name') }}
                    </label>
                    <input class="au-input au-input--full"
                           id="name"
                           :placeholder="text('enter_name')"
                           type="text"
                           v-model.trim="form.name">
                  </div>
                  <div class="col-6">
                    <label for="company">
                      {{ text('company') }}
                    </label>
                    <input class="au-input au-input--full"
                           id="company"
                           :placeholder="text('enter_company')"
                           type="text"
                           v-model.trim="form.company">
                  </div>
                </div>
                <div class="form-group">
                  <label for="password">
                    {{ text('password') }}*
                  </label>
                  <input @blur="$v.form.password.$touch()"
                         autocomplete="new-password"
                         class="au-input au-input--full"
                         id="password"
                         :placeholder="text('enter_password')"
                         type="password"
                         v-model="form.password">
                  <small class="form-text text-danger" v-if="$v.form.password.$dirty">
                    <span v-if="!$v.form.password.required">
                      {{ text('non_empty_err') }}
                    </span>
                    <span v-if="!$v.form.password.min">
                      {{ text('password_min_len_err') }}
                    </span>
                  </small>
                </div>
                <div class="form-group">
                  <label for="confirm-password">
                    {{ text('confirm_password') }}*
                  </label>
                  <input @blur="$v.form.confirmedPassword.$touch()"
                         autocomplete="new-password"
                         class="au-input au-input--full"
                         id="confirm-password"
                         :placeholder="text('repeat_password')"
                         type="password"
                         v-model="form.confirmedPassword">
                  <small class="form-text text-danger" v-if="$v.form.confirmedPassword.$dirty && !$v.form.confirmedPassword.sameAs">
                    {{ text('passwords_not_match_err') }}
                  </small>
                </div>
                <div :class="{invalid: $v.form.terms.$error}" class="login-checkbox">
                  <label>
                    <input @change="$v.form.terms.$touch()"
                           type="checkbox"
                           v-model="form.terms">{{ text('accept_terms') }}
                  </label>
                </div>
                <button class="au-btn au-btn--block au-btn--green m-b-20" type="submit">
                  <span class="spinner-border spinner-grow-sm"
                        style="margin-bottom: 4px; margin-right: 4px;"
                        v-if="isLoaded"></span>
                  {{ text('create_account') }}
                </button>
              </form>
              <div class="register-link">
                <p>
                  {{ text('already_have_account') }}
                  <router-link :to="{name: 'SignIn'}" tag="a">{{ text('sign_in') }}</router-link>
                </p>
              </div>
            </div>
          </div>
          <div class="card border border-success" v-if="registered">
            <div class="card-header">
              <strong class="card-title">{{ text('signup_confirmation') }}</strong>
            </div>
            <div class="card-body">
              <p class="card-text">
                {{ text('confirm_text') }}
              </p>
              <div class="register-link">
                <p>
                  <router-link :to="{name: 'SignIn'}" tag="a">{{ text('return_to_sign_in') }}</router-link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { email, minLength, required, sameAs } from 'vuelidate/lib/validators';

export default {
  data() {
    return {
      form: {
        email: '',
        name: '',
        company: '',
        password: '',
        confirmedPassword: '',
        terms: false,
      },
      error: '',
      isEmailUnique: true,
      registered: false,
      isLoaded: false,
    };
  },
  computed: {
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  validations: {
    form: {
      email: {
        required,
        email,
        isUnique(value) {
          if (value === null) {
            return true;
          }
          return new Promise((resolve) => {
            this.$store.dispatch('checkEmail', value)
              .then((res) => {
                this.isEmailUnique = !res.data;
                resolve(this.isEmailUnique);
              })
              .catch(() => {
                this.isEmailUnique = true;
                resolve(this.isEmailUnique);
              });
          });
        },
      },
      password: {
        required,
        min: minLength(6),
      },
      confirmedPassword: {
        sameAs: sameAs('password'),
      },
      terms: {
        sameAs: sameAs(() => true),
      },
    },
  },
  methods: {
    onSubmit() {
      this.error = '';
      this.$v.form.$touch();
      if (this.$v.form.$pending || this.$v.form.$error) return;

      const formData = {
        username: this.form.email,
        password: this.form.password,
        name: this.form.name,
        company: this.form.company,
      };

      this.isLoaded = true;
      this.$store.dispatch('signUp', formData)
        .then(() => {
          this.registered = true;
        })
        .catch((errorMsg) => {
          this.error = errorMsg;
        })
        .finally(() => {
          this.isLoaded = false;
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>

<style scoped>
  .invalid label {
    color: red;
  }
</style>
